import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Card,
  Upload,
  Button,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  Table,
  Tabs,
  Alert,
  Progress,
  message,
  Empty,
} from 'antd';
import {
  InboxOutlined,
  DownloadOutlined,
  ReloadOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  QuestionCircleOutlined,
  FileSearchOutlined,
} from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;
const { Dragger } = Upload;

// Create axios instance for admin API
const adminApi = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  timeout: 30000,
});

// Request interceptor to add JWT token
adminApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else if (process.env.NODE_ENV === 'development') {
      config.headers.Authorization = 'Bearer dummy-admin-token-for-testing';
    } else {
      window.location.href = '/admin/login';
      return Promise.reject(new Error('No authentication token'));
    }
    return config;
  },
  (error) => Promise.reject(error)
);

const rupiah = (n) =>
  'Rp ' + Number(n || 0).toLocaleString('id-ID', { maximumFractionDigits: 0 });

const KINDS = [
  {
    key: 'need_refund',
    label: 'Perlu Direfund',
    color: 'red',
    hint: 'Supplier menyatakan REFUND/GAGAL (dana sudah kembali ke Arendo), tapi di sistem kita masih PAID — mitra belum menerima pengembalian.',
  },
  {
    key: 'over_refund',
    label: 'Kelebihan Refund',
    color: 'orange',
    hint: 'Supplier menyatakan SUKSES, tapi di sistem kita sudah REFUNDED — mitra kemungkinan menerima pengembalian yang tidak seharusnya.',
  },
  {
    key: 'mismatch',
    label: 'Tidak Cocok Lainnya',
    color: 'gold',
    hint: 'Status kedua sisi tidak sejalan di luar dua kategori di atas.',
  },
  {
    key: 'unmatched',
    label: 'Belum Tercocokkan',
    color: 'purple',
    hint: 'Baris supplier yang belum bisa dipasangkan dengan transaksi kita, baik lewat Ref ID maupun rekening+nominal+waktu.',
  },
];

const SupplierReconciliation = () => {
  const [runId, setRunId] = useState(null);
  const [status, setStatus] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [activeKind, setActiveKind] = useState('need_refund');
  const [rows, setRows] = useState([]);
  const [rowsTotal, setRowsTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loadingRows, setLoadingRows] = useState(false);
  const pollRef = useRef(null);

  const summary = status?.summary;
  const done = status?.status === 'done';

  /* ---------- polling ---------- */
  useEffect(() => {
    if (!runId) return undefined;
    const tick = async () => {
      try {
        const { data } = await adminApi.get(`/admin/reconciliation/${runId}/status`);
        setStatus(data);
        if (data.status !== 'processing') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          if (data.status === 'error') message.error(data.error || 'Gagal memproses file');
        }
      } catch (e) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        message.error(e.response?.data?.error || 'Gagal mengambil status');
      }
    };
    tick();
    pollRef.current = setInterval(tick, 2000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [runId]);

  /* ---------- load rows once done / tab changes ---------- */
  useEffect(() => {
    if (!done || !runId) return;
    let cancelled = false;
    (async () => {
      setLoadingRows(true);
      try {
        const { data } = await adminApi.get(`/admin/reconciliation/${runId}/rows`, {
          params: { kind: activeKind, page, limit: 50 },
        });
        if (!cancelled) {
          setRows(data.rows || []);
          setRowsTotal(data.total || 0);
        }
      } catch (e) {
        if (!cancelled) message.error(e.response?.data?.error || 'Gagal mengambil data');
      } finally {
        if (!cancelled) setLoadingRows(false);
      }
    })();
    return () => { cancelled = true; };
  }, [done, runId, activeKind, page]);

  /* ---------- upload ---------- */
  const handleUpload = async (file) => {
    const form = new FormData();
    form.append('file', file);
    setUploading(true);
    setStatus(null);
    setRows([]);
    setPage(1);
    try {
      const { data } = await adminApi.post('/admin/reconciliation/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      setRunId(data.run_id);
      message.success('File diterima, sedang diproses…');
    } catch (e) {
      message.error(e.response?.data?.error || 'Gagal mengunggah file');
    } finally {
      setUploading(false);
    }
    return false; // prevent antd's own upload
  };

  const handleDownload = async () => {
    try {
      const res = await adminApi.get(`/admin/reconciliation/${runId}/download`, {
        responseType: 'blob',
        timeout: 300000,
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = (status?.file_name || 'laporan').replace(/\.csv$/i, '') + '_dengan_Status_Sistem.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      message.error('Gagal mengunduh berkas hasil');
    }
  };

  const reset = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
    setRunId(null);
    setStatus(null);
    setRows([]);
    setRowsTotal(0);
    setPage(1);
    setActiveKind('need_refund');
  };

  /* ---------- table ---------- */
  const columns = [
    { title: 'Tanggal', dataIndex: 'Tanggal', width: 150, fixed: 'left' },
    { title: 'Produk', dataIndex: 'Produk', width: 100 },
    {
      title: 'Status Supplier',
      dataIndex: 'Status',
      width: 120,
      render: (v) => (
        <Tag color={v === 'SUKSES' ? 'green' : v === 'REFUND' ? 'red' : 'orange'}>{v}</Tag>
      ),
    },
    {
      title: 'Tagihan',
      dataIndex: 'Tagihan',
      width: 130,
      align: 'right',
      render: (v) => rupiah(v),
    },
    { title: 'Ref ID Supplier', dataIndex: 'Ref ID', width: 190 },
    { title: 'Ref ID Sistem', dataIndex: 'Ref ID Sistem (Transaction ID)', width: 250 },
    { title: 'Mitra Ref ID', dataIndex: 'Mitra Ref ID (Order ID)', width: 185 },
    { title: 'Mitra', dataIndex: 'User ID Mitra', width: 70, align: 'center' },
    {
      title: 'Status Sistem',
      dataIndex: 'Status Sistem',
      width: 120,
      render: (v) =>
        v ? <Tag color={v === 'PAID' ? 'blue' : v === 'REFUNDED' ? 'green' : 'default'}>{v}</Tag> : '-',
    },
    {
      title: 'Nett Saldo Mitra',
      dataIndex: 'Nett Saldo Mitra',
      width: 150,
      align: 'right',
      render: (v) =>
        v === '' || v === undefined || v === null ? '-' : (
          <Text style={{ color: Number(v) < 0 ? '#cf1322' : '#389e0d' }}>
            {Number(v) === 0 ? 'Rp 0 (dikembalikan)' : rupiah(v)}
          </Text>
        ),
    },
    {
      title: 'Refund ke Mitra',
      dataIndex: 'Refund ke Mitra Sudah Ada?',
      width: 120,
      align: 'center',
      render: (v) => (v === 'YA' ? <Tag color="green">SUDAH</Tag> : v === 'BELUM' ? <Tag color="red">BELUM</Tag> : '-'),
    },
    { title: 'Cara Pencocokan', dataIndex: 'Cara Pencocokan', width: 260 },
    { title: 'Hasil Matching', dataIndex: 'HASIL MATCHING', width: 420 },
  ];

  const pct =
    status?.progress?.total > 0
      ? Math.min(99, Math.round((status.progress.done / status.progress.total) * 100))
      : null;

  return (
    <div>
      <Title level={3}>
        <FileSearchOutlined /> Rekonsiliasi Laporan Supplier
      </Title>
      <Paragraph type="secondary">
        Unggah laporan transaksi mentah dari LoketBayar (CSV). Sistem akan mencocokkan setiap baris
        dengan transaksi kita dan menunjukkan mana saja refund dari supplier yang{' '}
        <b>belum diteruskan ke mitra</b>. Fitur ini hanya membaca data — tidak mengubah saldo
        maupun status transaksi apa pun.
      </Paragraph>

      {!runId && (
        <Card>
          <Dragger
            accept=".csv"
            maxCount={1}
            showUploadList={false}
            beforeUpload={handleUpload}
            disabled={uploading}
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">Klik atau seret berkas CSV ke sini</p>
            <p className="ant-upload-hint">
              Laporan transaksi LoketBayar (mis. Laporan_Transaksi_LMB010755_…csv). Maksimal 120 MB.
              Berkas besar (±190 ribu baris) butuh sekitar satu menit.
            </p>
          </Dragger>
        </Card>
      )}

      {runId && status?.status === 'processing' && (
        <Card>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text strong>Memproses {status.file_name}…</Text>
            <Text type="secondary">Tahap: {status.phase}</Text>
            <Progress percent={pct ?? 0} status="active" showInfo={pct !== null} />
            <Text type="secondary">
              {status.progress?.done
                ? `${Number(status.progress.done).toLocaleString('id-ID')} baris diproses`
                : 'Menyiapkan…'}
            </Text>
          </Space>
        </Card>
      )}

      {status?.status === 'error' && (
        <Card>
          <Alert
            type="error"
            showIcon
            message="Gagal memproses berkas"
            description={status.error}
          />
          <Button style={{ marginTop: 16 }} icon={<ReloadOutlined />} onClick={reset}>
            Coba berkas lain
          </Button>
        </Card>
      )}

      {done && summary && (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic title="Total Baris Laporan" value={summary.totalRows} />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  cocok via Ref ID: {summary.matchedByRef.toLocaleString('id-ID')} · via
                  rekening+nominal: {summary.matchedByFallback.toLocaleString('id-ID')}
                </Text>
                {(summary.duplicatedRefs > 0 || summary.refRejectedByAmount > 0) && (
                  <>
                    <br />
                    <Text type="warning" style={{ fontSize: 12 }}>
                      Ref ID dipakai ganda oleh supplier: {summary.duplicatedRefs} · ditolak karena
                      nominal tak cocok: {summary.refRejectedByAmount}
                    </Text>
                  </>
                )}
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Perlu Direfund ke Mitra"
                  value={summary.needRefundCount}
                  valueStyle={{ color: '#cf1322' }}
                  prefix={<WarningOutlined />}
                />
                <Text strong style={{ color: '#cf1322' }}>
                  {rupiah(summary.needRefundAmount)}
                </Text>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Kelebihan Refund"
                  value={summary.overRefundCount || 0}
                  valueStyle={{ color: '#d46b08' }}
                  prefix={<CheckCircleOutlined />}
                />
                <Text strong style={{ color: '#d46b08' }}>
                  {rupiah(summary.overRefundAmount)}
                </Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  supplier REFUND: {(summary.supplierStatus?.REFUND || 0).toLocaleString('id-ID')} ·
                  GAGAL: {(summary.supplierStatus?.GAGAL || 0).toLocaleString('id-ID')}
                </Text>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Belum Tercocokkan"
                  value={summary.unmatched}
                  prefix={<QuestionCircleOutlined />}
                />
                <Text type="secondary" style={{ fontSize: 12 }}>perlu penelusuran manual</Text>
              </Card>
            </Col>
          </Row>

          <Card
            title={`Hasil: ${status.file_name}`}
            extra={
              <Space>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  hasil tersimpan {Math.round((status.expires_in_sec || 0) / 60)} menit lagi
                </Text>
                <Button icon={<DownloadOutlined />} type="primary" onClick={handleDownload}>
                  Unduh CSV Lengkap
                </Button>
                <Button icon={<ReloadOutlined />} onClick={reset}>Unggah Lain</Button>
              </Space>
            }
          >
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="CSV lengkap berisi seluruh baris laporan supplier dengan kolom asli yang tidak diubah, ditambah kolom status sistem kita."
            />

            <Tabs
              activeKey={activeKind}
              onChange={(k) => { setActiveKind(k); setPage(1); }}
              items={KINDS.map((k) => ({
                key: k.key,
                label: (
                  <span>
                    {k.label}{' '}
                    <Tag color={k.color}>
                      {Object.entries(summary.verdicts || {})
                        .filter(([text]) =>
                          k.key === 'need_refund' ? text.startsWith('PERLU TINDAKAN')
                            : k.key === 'over_refund' ? text.includes('kelebihan refund')
                            : k.key === 'unmatched' ? text.startsWith('BELUM TERCOCOKKAN')
                            : text.startsWith('TIDAK COCOK') && !text.includes('kelebihan refund'))
                        .reduce((a, [, n]) => a + n, 0)}
                    </Tag>
                  </span>
                ),
                children: (
                  <>
                    <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={k.hint} />
                    <Table
                      size="small"
                      rowKey={(r, i) => `${r['Ref ID']}-${i}`}
                      columns={columns}
                      dataSource={rows}
                      loading={loadingRows}
                      scroll={{ x: 2200 }}
                      locale={{ emptyText: <Empty description="Tidak ada baris pada kategori ini" /> }}
                      pagination={{
                        current: page,
                        pageSize: 50,
                        total: rowsTotal,
                        showSizeChanger: false,
                        onChange: setPage,
                        showTotal: (t) => `${t.toLocaleString('id-ID')} baris`,
                      }}
                    />
                  </>
                ),
              }))}
            />
          </Card>
        </>
      )}
    </div>
  );
};

export default SupplierReconciliation;
